import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as admin from '../admin'
import * as common from '../common'
import * as dms from '../dms'

import type * as agent from './agent'

/**
 * Identifies a Bedrock knowledge base that can be attached to one or more
 * agents.
 *
 * Values must match the `id` passed to `KnowledgeBase.define()` in
 * `@attain-apps-be/bedrock-agent-defs`.
 */
export enum KnowledgeBase {
  DolomiteGeneral = 'dolomite-general',
  GilResearchIndirectCost = 'gil-research-indirect-cost',
  OcmAscend = 'ocm-ascend',
  OcmAscendTranscript = 'ocm-ascend-transcript',
  OcmChangeManagement = 'ocm-change-management',
  PrincetonAscend = 'princeton-ascend',
  ProposalMatrix = 'proposal-matrix',
  PptGenerator = 'ppt-generator',
  /** Shared source for tenant, zone, and DMS-record documents used by eligible agents. */
  SharedScoped = 'shared-scoped',
}

const KNOWLEDGE_BASES: ReadonlySet<KnowledgeBase> = new Set(
  Object.values(KnowledgeBase) as KnowledgeBase[],
)

export namespace KnowledgeBase {
  /** All knowledge bases defined by the application. */
  export const ALL: readonly KnowledgeBase[] = Array.from(KNOWLEDGE_BASES)

  /** Checks whether a value identifies a defined knowledge base. */
  export const isKnowledgeBase = TypeChecker.Factory.oneOf(KNOWLEDGE_BASES)

  /** Asserts that a value identifies a defined knowledge base. */
  export const assertIsKnowledgeBase: TypeAsserter<KnowledgeBase> =
    TypeAsserter.Factory.from(isKnowledgeBase)

  /**
   * Identifies the exact audience whose agent invocations can retrieve a
   * knowledge-base document. Platform documents belong to agent-specific
   * sources; narrower targets belong to the shared scoped source.
   */
  export type Target =
    | Target.Platform
    | Target.Tenant
    | Target.Zone
    | Target.Record

  export namespace Target {
    /** Available kinds of knowledge-base document audience. */
    export enum Type {
      /* eslint-disable @typescript-eslint/no-shadow */
      Platform = 'platform',
      Tenant = 'tenant',
      Zone = 'zone',
      Record = 'record',
      /* eslint-enable @typescript-eslint/no-shadow */
    }

    /**
     * Makes a document available to agent invocations throughout the platform
     * without granting access to documents with narrower targets. These
     * documents remain in an agent-specific knowledge base.
     */
    export interface Platform {
      readonly type: Type.Platform
    }
    export namespace Platform {
      /** Checks whether a value represents platform-wide availability. */
      export const isPlatform = TypeChecker.Factory.object<Platform>({
        type: TypeChecker.Factory.exact(Type.Platform),
      })
    }

    /**
     * Makes a shared scoped document available to invocations in one exact
     * tenant.
     */
    export interface Tenant extends admin.Context.ForTenant {
      readonly type: Type.Tenant
    }
    export namespace Tenant {
      /** Checks whether a value targets one exact tenant. */
      export const isTenant: TypeChecker<Tenant> =
        TypeChecker.Factory.intersection(
          admin.Context.ForTenant.isForTenant,
          TypeChecker.Factory.object<
            Difference<Tenant, admin.Context.ForTenant>
          >({
            type: TypeChecker.Factory.exact(Type.Tenant),
          }),
        )
    }

    /**
     * Makes a shared scoped document available to invocations in one exact
     * zone.
     */
    export interface Zone extends admin.Context.ForZone {
      readonly type: Type.Zone
    }
    export namespace Zone {
      /** Checks whether a value targets one exact zone. */
      export const isZone: TypeChecker<Zone> = TypeChecker.Factory.intersection(
        admin.Context.ForZone.isForZone,
        TypeChecker.Factory.object<Difference<Zone, admin.Context.ForZone>>({
          type: TypeChecker.Factory.exact(Type.Zone),
        }),
      )
    }

    /**
     * Makes a shared scoped document available only when an invocation is
     * authorized for one exact DMS record in its tenant and zone.
     */
    export interface Record extends admin.Context.ForZone {
      readonly modelId: dms.models.ForRecord.Id
      readonly recordId: dms.data.Record.Id
      readonly type: Type.Record
    }
    export namespace Record {
      /** Checks whether a value targets one exact DMS record. */
      export const isRecord: TypeChecker<Record> =
        TypeChecker.Factory.intersection(
          admin.Context.ForZone.isForZone,
          TypeChecker.Factory.object<Difference<Record, admin.Context.ForZone>>(
            {
              modelId: dms.models.ForRecord.Id.isId,
              recordId: dms.data.Record.Id.isId,
              type: TypeChecker.Factory.exact(Type.Record),
            },
          ),
        )
    }

    /** Checks whether a value identifies one supported document audience. */
    export const isTarget: TypeChecker<Target> = TypeChecker.Factory.union(
      Platform.isPlatform,
      Tenant.isTenant,
      Zone.isZone,
      Record.isRecord,
    ).usingAlias('KnowledgeBase.Target')

    /**
     * Canonical target path embedded in managed keys and stored as the
     * filtering value when a document belongs to the shared scoped knowledge
     * base.
     */
    export type Path = string
    export namespace Path {
      /** Folders that identify the target represented by a canonical path. */
      export enum ParentFolder {
        /* eslint-disable @typescript-eslint/no-shadow */
        Model = 'M',
        Platform = 'P',
        Record = 'R',
        Tenant = 'T',
        Zone = 'Z',
        /* eslint-enable @typescript-eslint/no-shadow */
      }

      /** Builds the canonical path for one exact document target. */
      export function from(target: Target): Path {
        let segments: string[]
        switch (target.type) {
          case Type.Platform:
            segments = [ParentFolder.Platform]
            break
          case Type.Tenant:
            segments = [ParentFolder.Tenant, target.tenantId]
            break
          case Type.Zone:
            segments = [
              ParentFolder.Tenant,
              target.tenantId,
              ParentFolder.Zone,
              target.zoneId,
            ]
            break
          case Type.Record:
            segments = [
              ParentFolder.Tenant,
              target.tenantId,
              ParentFolder.Zone,
              target.zoneId,
              ParentFolder.Model,
              target.modelId,
              ParentFolder.Record,
              target.recordId,
            ]
            break
          default:
            throw TypeAsserter.assertIsNever(target)
        }
        return segments.join('/')
      }
    }

    /** Bedrock metadata attribute used to filter the shared scoped source. */
    export const METADATA_ATTRIBUTE_NAME = 'atnKnowledgeBaseTarget'
  }

  /**
   * {@link dms.data.Record DMS Records} already authorized for knowledge-base retrieval during one
   * agent invocation. Tenant and zone availability comes from the invocation's
   * trusted backend context.
   */
  export interface Retrieval {
    /** {@link Retrieval.Record} */
    readonly records?: readonly Retrieval.Record[]
  }

  export namespace Retrieval {
    /**
     * Identifies one authorized {@link dms.data.Record} together with the
     * {@link dms.models.ForRecord} that defines its structure.
     */
    export type Record = Pick<Target.Record, 'modelId' | 'recordId'>
  }

  /**
   * Inventory of knowledge-base sources available to Platform Admin users.
   *
   * Agent-specific sources are grouped by their owning agent, while the shared
   * scoped source is represented once because it can serve multiple agents.
   */
  export interface Management {
    /** Agents that own one or more private knowledge-base data sources. */
    readonly agents: readonly Management.Agent[]
    /** Singleton source for documents shared across eligible agents. */
    readonly sharedDataSource: Management.DataSource
  }

  export namespace Management {
    /** Agent whose private knowledge-base sources can be managed. */
    export interface Agent {
      /** Identifies the agent that owns the private sources. */
      readonly agentId: agent.Agent.Id
      /** User-facing name of the agent whose sources are being managed. */
      readonly agentName: string
      /** Private data sources whose files can be managed for this agent. */
      readonly dataSources: readonly DataSource[]
    }

    /** File extensions accepted for documents added through the Admin Center. */
    export const SUPPORTED_FILE_EXTENSIONS = [
      '.txt',
      '.md',
      '.html',
      '.doc',
      '.docx',
      '.csv',
      '.xls',
      '.xlsx',
      '.pdf',
    ] as const

    /** Maximum size of a document added through the Admin Center. */
    export const MAXIMUM_FILE_SIZE_BYTES = 50 * 1024 * 1024

    /**
     * Data-source identifier for the singleton {@link KnowledgeBase.SharedScoped}
     * source that stores Tenant, Zone, and DMS-record documents.
     */
    export const SHARED_SCOPED_DATA_SOURCE_ID = 'scoped-documents'

    /**
     * S3 object key for a document managed through the knowledge-base Admin
     * Center.
     */
    export type Key = common.fileManagement.Key
    export namespace Key {
      const METADATA_COMPANION_SUFFIX = '.metadata.json'

      /**
       * S3 prefix containing documents managed through the knowledge-base
       * Admin Center in either an agent-specific or shared scoped source.
       */
      export const PREFIX: Key = `platform/${common.fileManagement.Purpose.FileManagement}`

      /** Information encoded in a managed knowledge-base document key. */
      export interface Parts {
        /** Original document name stored at the end of the key. */
        readonly name: string
        /** Audience represented by the key's target folders. */
        readonly target: Target
      }
      export namespace Parts {
        /** Managed document key for one exact Tenant or Zone audience. */
        export interface TenantOrZone extends Parts {
          readonly target: Target.Tenant | Target.Zone
        }
      }

      /**
       * Builds the managed key for a validated document name and its exact
       * availability target.
       */
      export function from({ name, target }: Parts): Key {
        return [PREFIX, Target.Path.from(target), name].join('/')
      }

      /**
       * Builds the S3 listing prefix for all managed documents or one exact
       * shared audience.
       */
      export function getPrefix(target?: Target.Tenant | Target.Zone): Key {
        const segments = [PREFIX]
        if (target) {
          segments.push(Target.Path.from(target))
        }
        return [...segments, ''].join('/')
      }

      /** Returns whether a key belongs to the Admin-managed document prefix. */
      export function isManaged(key: Key): boolean {
        return key.startsWith(getPrefix())
      }

      /** Reads the file name stored at the end of an S3 object key. */
      export function getName(key: Key): string | undefined {
        return key.split('/').at(-1) ?? undefined
      }

      /**
       * Reads a managed document key. Legacy flat keys are treated as
       * platform-targeted documents.
       */
      export function parse(key: Key): Parts | undefined {
        const prefix = getPrefix()
        if (!key.startsWith(prefix)) {
          return undefined
        }

        const managedPath = key.slice(prefix.length)
        if (!managedPath) {
          return undefined
        }

        const segments = managedPath.split('/')
        const name = segments.pop()
        if (!name) {
          return undefined
        }

        if (segments.length === 0) {
          return { name, target: { type: Target.Type.Platform } }
        }

        const [
          targetParentFolder,
          tenantId,
          zoneParentFolder,
          zoneId,
          modelParentFolder,
          modelId,
          recordParentFolder,
          recordId,
        ] = segments
        let target: unknown
        switch (segments.length) {
          case 1:
            target =
              targetParentFolder === Target.Path.ParentFolder.Platform
                ? { type: Target.Type.Platform }
                : undefined
            break
          case 2:
            target =
              targetParentFolder === Target.Path.ParentFolder.Tenant
                ? {
                    tenantId,
                    type: Target.Type.Tenant,
                  }
                : undefined
            break
          case 4:
            target =
              targetParentFolder === Target.Path.ParentFolder.Tenant &&
              zoneParentFolder === Target.Path.ParentFolder.Zone
                ? {
                    tenantId,
                    type: Target.Type.Zone,
                    zoneId,
                  }
                : undefined
            break
          case 8:
            target =
              targetParentFolder === Target.Path.ParentFolder.Tenant &&
              zoneParentFolder === Target.Path.ParentFolder.Zone &&
              modelParentFolder === Target.Path.ParentFolder.Model &&
              recordParentFolder === Target.Path.ParentFolder.Record
                ? {
                    modelId,
                    recordId,
                    tenantId,
                    type: Target.Type.Record,
                    zoneId,
                  }
                : undefined
            break
        }
        return Target.isTarget(target) ? { name, target } : undefined
      }

      /**
       * Reads a Tenant- or Zone-targeted source document while excluding
       * Bedrock metadata companions and narrower DMS Record documents.
       */
      export function parseTenantOrZone(
        key: Key,
      ): Parts.TenantOrZone | undefined {
        if (key.endsWith('/') || key.endsWith(METADATA_COMPANION_SUFFIX)) {
          return undefined
        }

        const parts = parse(key)
        return parts?.target.type === Target.Type.Tenant ||
          parts?.target.type === Target.Type.Zone
          ? { name: parts.name, target: parts.target }
          : undefined
      }
    }

    /** An existing agent knowledge-base data source shown in the Admin Center. */
    export interface DataSource {
      /** User-facing explanation of the documents this source contributes. */
      readonly description?: string
      /** Stable data-source identifier from the knowledge-base definition. */
      readonly id: string
      /** Knowledge base that ingests documents from this source. */
      readonly knowledgeBaseId: KnowledgeBase
    }

    export namespace DataSource {
      /**
       * Identifies the knowledge-base data source whose managed documents are
       * being displayed in the Admin Center.
       */
      export interface Target {
        /** Stable identifier of the data source containing the documents. */
        readonly dataSourceId: string
        /** Knowledge base that ingests documents from the data source. */
        readonly knowledgeBaseId: KnowledgeBase
      }
    }

    /** A source document in an existing knowledge-base data-source bucket. */
    export interface File {
      /**
       * Whether the document can be deleted through the Admin Center.
       * Repository-deployed documents are retained and return `false`.
       */
      readonly canDelete: boolean
      /** Data source containing the document. */
      readonly dataSourceId: string
      /** S3 object key used to address the document within its source bucket. */
      readonly key: Key
      /** Knowledge base that ingests the document. */
      readonly knowledgeBaseId: KnowledgeBase
      /** ISO timestamp of the most recent S3 object change, when available. */
      readonly lastModifiedAt?: string
      /** User-facing document name. */
      readonly name: string
      /** Size of the stored document in bytes. */
      readonly sizeBytes: number
    }

    /**
     * Selects the source whose logical documents are listed in the Admin
     * Center.
     */
    export type FileListInput =
      | FileListInput.Private
      | FileListInput.Tenant
      | FileListInput.Zone

    export namespace FileListInput {
      /** Lists one agent-specific private data source. */
      export interface Private extends DataSource.Target {
        readonly target?: never
      }

      /** Lists the singleton shared source for Tenant documents. */
      export interface Tenant {
        readonly dataSourceId?: never
        readonly knowledgeBaseId?: never
        readonly target: Target.Tenant
      }

      /** Lists the singleton shared source for Zone documents. */
      export interface Zone {
        readonly dataSourceId?: never
        readonly knowledgeBaseId?: never
        readonly target: Target.Zone
      }

      /** Checks whether a value is a valid management file-list request. */
      export const isFileListInput: TypeChecker<FileListInput> =
        TypeChecker.Factory.union(
          TypeChecker.Factory.object<Private>({
            dataSourceId: TypeChecker.isString,
            knowledgeBaseId: isKnowledgeBase,
            target: TypeChecker.isUndefined,
          }),
          TypeChecker.Factory.object<Tenant>({
            dataSourceId: TypeChecker.isUndefined,
            knowledgeBaseId: TypeChecker.isUndefined,
            target: Target.Tenant.isTenant,
          }),
          TypeChecker.Factory.object<Zone>({
            dataSourceId: TypeChecker.isUndefined,
            knowledgeBaseId: TypeChecker.isUndefined,
            target: Target.Zone.isZone,
          }),
        ).usingAlias('KnowledgeBase.Management.FileListInput')

      /** Asserts that a value is a valid management file-list request. */
      export const assertIsFileListInput: TypeAsserter<FileListInput> =
        TypeAsserter.Factory.from(isFileListInput)
    }

    /**
     * Identifies an existing managed source document for download or deletion.
     */
    export interface FileTarget {
      /** Data source containing the document. */
      readonly dataSourceId: string
      /** Exact S3 object key returned when the source documents were listed. */
      readonly key: Key
      /** Knowledge base that ingests the document. */
      readonly knowledgeBaseId: KnowledgeBase
    }

    /**
     * Describes a document that a Platform Admin user wants to add to either a
     * private agent source or the shared scoped source.
     */
    export type UploadInput = UploadInput.Private | UploadInput.Shared
    export namespace UploadInput {
      /** Upload routed to one explicitly selected private agent source. */
      export interface Private extends DataSource.Target {
        /** Original document name used for its managed S3 object key. */
        readonly name: string
        /** Document size used to ensure it is within the supported limit. */
        readonly sizeBytes: number
        /** Private uploads are not routed by a shared document audience. */
        readonly target?: never
      }

      /** Upload routed to the singleton shared source by its exact audience. */
      export interface Shared {
        /** Shared routing is resolved by the backend from the target. */
        readonly dataSourceId?: never
        /** Shared routing is resolved by the backend from the target. */
        readonly knowledgeBaseId?: never
        /** Original document name used for its managed S3 object key. */
        readonly name: string
        /** Document size used to ensure it is within the supported limit. */
        readonly sizeBytes: number
        /** Exact Tenant, Zone, or Record audience that can retrieve the document. */
        readonly target: Target.Tenant | Target.Zone | Target.Record
      }

      /** Checks whether a name is safe and supported for knowledge-base use. */
      export const isName = TypeChecker.Factory.custom(
        (value: unknown): value is string => {
          if (
            typeof value !== 'string' ||
            value === '' ||
            value.trim() !== value ||
            value === '.' ||
            value === '..' ||
            value.includes('/') ||
            value.includes('\\') ||
            Array.from(value).some(character => {
              const characterCode = character.charCodeAt(0)
              return characterCode <= 31 || characterCode === 127
            })
          ) {
            return false
          }

          const dotIndex = value.lastIndexOf('.')
          const extension =
            dotIndex > 0 ? value.slice(dotIndex).toLowerCase() : ''
          return SUPPORTED_FILE_EXTENSIONS.some(
            supportedExtension => supportedExtension === extension,
          )
        },
        { typeDescription: 'supported knowledge-base file name' },
      )

      /** Checks whether document content fits the accepted upload range. */
      export const isSizeBytes = TypeChecker.Factory.custom(
        (value: unknown): value is number =>
          typeof value === 'number' &&
          Number.isInteger(value) &&
          value >= 1 &&
          value <= MAXIMUM_FILE_SIZE_BYTES,
        { typeDescription: 'supported knowledge-base file size' },
      )

      export namespace Private {
        /** Checks whether an upload targets one private agent source. */
        export const isPrivate = TypeChecker.Factory.object<Private>({
          dataSourceId: TypeChecker.isString,
          knowledgeBaseId: isKnowledgeBase,
          name: isName,
          sizeBytes: isSizeBytes,
          target: TypeChecker.isUndefined,
        })
      }

      export namespace Shared {
        /** Checks whether an upload targets the shared Tenant, Zone, or Record source. */
        export const isShared = TypeChecker.Factory.object<Shared>({
          dataSourceId: TypeChecker.isUndefined,
          knowledgeBaseId: TypeChecker.isUndefined,
          name: isName,
          sizeBytes: isSizeBytes,
          target: TypeChecker.Factory.union(
            Target.Tenant.isTenant,
            Target.Zone.isZone,
            Target.Record.isRecord,
          ),
        })
      }

      /** Checks whether a value is one supported management upload request. */
      export const isUploadInput: TypeChecker<UploadInput> =
        TypeChecker.Factory.union(
          Private.isPrivate,
          Shared.isShared,
        ).usingAlias('KnowledgeBase.Management.UploadInput')

      /** Asserts that a value is one supported management upload request. */
      export const assertIsUploadInput: TypeAsserter<UploadInput> =
        TypeAsserter.Factory.from(isUploadInput)
    }
  }

  export interface File {
    knowledgeBaseId: string
    s3Uri: string
  }
  export namespace File {
    /** Returns the object path portion of the file's S3 URI. */
    export function getPath(file: File): string {
      return file.s3Uri.replace(/^s3:\/\/[^/]+\//, '')
    }

    /**
     * Two files are equal if they have the same knowledge-base ID and S3 URI,
     * even if they're different object instances.
     */
    export function isEqual(a: File, b: File): boolean {
      return a.knowledgeBaseId === b.knowledgeBaseId && a.s3Uri === b.s3Uri
    }
  }
}
